import { ATTACHMENT_SRC_RE, CONTROL_CHAR_RE } from "./blocks.ts";
import { mergeToInline, type TiptapDoc, type TiptapNode } from "./convert.ts";
import { DOC_CODE_RE } from "./doc-types.ts";
import { toSafeHref } from "./inline.ts";

// normalizeTiptap turns any JSON (pasted content, API input, older documents) into a
// document that fits contract C3 exactly, so tiptapToBlocks accepts it. It never throws.

const MAX_DEPTH = 64;
const MAX_COLUMNS = 50;

type Obj = Record<string, unknown>;
const isObj = (x: unknown): x is Obj => typeof x === "object" && x !== null && !Array.isArray(x);
const list = (x: unknown): unknown[] => (Array.isArray(x) ? x : []);
const attrsOf = (n: Obj): Obj => (isObj(n.attrs) ? n.attrs : {});
const CONTROL_CHARS = new RegExp(CONTROL_CHAR_RE.source, "g");
/** Strip C0 control characters (C1); vertical tab and form feed become a space. */
const clean = (s: string): string => s.replace(/[\v\f]/g, " ").replace(CONTROL_CHARS, "");
/** A non-empty string with control characters stripped, or null. */
const str = (x: unknown): string | null => {
  const s = typeof x === "string" ? clean(x) : "";
  return s !== "" ? s : null;
};

const INLINE_TYPES = new Set(["text", "hardBreak", "docLink", "placeholder"]);
const MARK_PRIORITY = ["link", "code", "bold", "italic"];

const emptyParagraph = (): TiptapNode => ({ type: "paragraph" });
const paragraph = (content: TiptapNode[]): TiptapNode =>
  content.length ? { type: "paragraph", content } : { type: "paragraph" };
const textNode = (text: string): TiptapNode[] => (text ? [{ type: "text", text }] : []);

/** Keep at most one allowed mark: link (href repaired by toSafeHref) first, then code, bold, italic. */
function normMarks(marks: unknown): TiptapNode["marks"] {
  const found = list(marks).filter(isObj);
  for (const type of MARK_PRIORITY) {
    const m = found.find((x) => x.type === type);
    if (!m) continue;
    if (type !== "link") return [{ type }];
    const raw = attrsOf(m).href;
    const href = typeof raw === "string" ? toSafeHref(raw) : null;
    if (href) return [{ type: "link", attrs: { href } }];
  }
  return undefined;
}

/** Inline content: text (one mark), docLink, placeholder. hardBreak becomes a space; other nodes give their inline content. */
function normInline(content: unknown, depth: number): TiptapNode[] {
  const out: TiptapNode[] = [];
  const push = (node: TiptapNode) => {
    const prev = out[out.length - 1];
    if (
      prev?.type === "text" &&
      node.type === "text" &&
      JSON.stringify(prev.marks ?? null) === JSON.stringify(node.marks ?? null)
    ) {
      prev.text = `${prev.text ?? ""}${node.text ?? ""}`;
      return;
    }
    out.push(node);
  };
  for (const n of list(content)) {
    if (!isObj(n) || depth > MAX_DEPTH) continue;
    const attrs = attrsOf(n);
    switch (n.type) {
      case "text": {
        const text = str(n.text);
        if (!text) break;
        const marks = normMarks(n.marks);
        push(marks ? { type: "text", text, marks } : { type: "text", text });
        break;
      }
      case "hardBreak":
        push({ type: "text", text: " " });
        break;
      case "docLink": {
        const code = str(attrs.code);
        const text = str(attrs.text);
        if (code && DOC_CODE_RE.test(code)) push({ type: "docLink", attrs: { code, text: text ?? code } });
        else if (text) push({ type: "text", text });
        break;
      }
      case "placeholder": {
        const label = str(attrs.label);
        if (label) push({ type: "placeholder", attrs: { label } });
        break;
      }
      default:
        for (const child of normInline(n.content, depth + 1)) push(child);
    }
  }
  return out;
}

/** All visible text below a node, as plain text. */
function plainText(content: unknown, depth: number, sep = ""): string {
  const parts: string[] = [];
  for (const n of list(content)) {
    if (!isObj(n) || depth > MAX_DEPTH) continue;
    const attrs = attrsOf(n);
    if (n.type === "text") parts.push(typeof n.text === "string" ? clean(n.text) : "");
    else if (n.type === "hardBreak") parts.push("\n");
    else if (n.type === "docLink") parts.push(str(attrs.text) ?? str(attrs.code) ?? "");
    else if (n.type === "placeholder") parts.push(str(attrs.label) ?? "");
    else parts.push(plainText(n.content, depth + 1, sep));
  }
  return parts.join(sep);
}

/** Inline content of normalised blocks, joined with a space: used where exactly one paragraph is allowed. */
function mergeBlocks(blocks: TiptapNode[]): TiptapNode[] {
  return normInline(
    mergeToInline(blocks, (b) => plainText(b.content, 0, " ")),
    0,
  );
}

const codeLines = (text: string): string[] => {
  const lines = text.split(/\r\n|\r|\n/);
  const kept = lines.length > 1 ? lines.filter((l) => l !== "") : lines;
  return kept.length ? kept : [""];
};

/** Cell content: paragraphs and klCodeLines only. */
function normCellContent(content: unknown, depth: number): TiptapNode[] {
  const out: TiptapNode[] = [];
  for (const b of normBlocks(content, depth)) {
    if (b.type === "paragraph") out.push(b);
    else if (b.type === "codeBlock") {
      const text = plainText(b.content, 0);
      out.push(text ? { type: "klCodeLine", content: textNode(text) } : { type: "klCodeLine" });
    } else if (b.type === "bulletList" || b.type === "orderedList") {
      for (const li of b.content ?? []) out.push(...(li.content ?? []));
    } else {
      const text = plainText(b.content, 0, " ");
      if (text) out.push(paragraph(textNode(text)));
    }
  }
  return out.length ? out : [emptyParagraph()];
}

function listItems(content: unknown, depth: number): TiptapNode[] {
  const paragraphs: TiptapNode[] = [];
  for (const item of list(content)) {
    if (!isObj(item)) continue;
    const inner = item.type === "listItem" ? item.content : [item];
    const before = paragraphs.length;
    for (const b of normBlocks(inner, depth + 1)) {
      if (b.type === "paragraph") paragraphs.push(b);
      else if (b.type === "bulletList" || b.type === "orderedList") {
        for (const li of b.content ?? []) paragraphs.push(...(li.content ?? []));
      } else {
        const text = plainText(b.content, 0, " ");
        if (text) paragraphs.push(paragraph(textNode(text)));
      }
    }
    if (item.type === "listItem" && paragraphs.length === before) paragraphs.push(emptyParagraph());
  }
  return paragraphs.map((p) => ({ type: "listItem", content: [p] }));
}

function buildTable(rows: TiptapNode[][], widths: unknown, firstColBold: boolean): TiptapNode[] {
  if (!rows.length) return [];
  const n = Math.min(
    MAX_COLUMNS,
    rows.reduce((max, r) => Math.max(max, r.length), 0),
  );
  if (n < 1) return [];
  const pad = (r: TiptapNode[]) => [
    ...r.slice(0, n),
    ...Array.from({ length: Math.max(0, n - r.length) }, () => ({ type: "klTableCell", content: [emptyParagraph()] })),
  ];
  const [head = [], ...body] = rows;
  const header = pad(head).map((cell) => ({
    type: "klTableCell",
    content: [paragraph(mergeBlocks(cell.content ?? []))],
  }));
  const validWidths =
    Array.isArray(widths) &&
    widths.length === n &&
    widths.every((w) => typeof w === "number" && Number.isFinite(w) && w > 0)
      ? [...widths]
      : null;
  return [
    {
      type: "klTable",
      attrs: { widths: validWidths, firstColBold },
      content: [
        { type: "klTableHeaderRow", content: header },
        ...body.map((r) => ({ type: "klTableRow", content: pad(r) })),
      ],
    },
  ];
}

function cell(content: unknown, depth: number): TiptapNode {
  return { type: "klTableCell", content: normCellContent(content, depth) };
}

/** klTable: rows of klTableCell, padded to the widest row. */
function normKlTable(n: Obj, depth: number): TiptapNode[] {
  const rows = list(n.content)
    .filter(isObj)
    .filter((r) => r.type === "klTableHeaderRow" || r.type === "klTableRow")
    .map((r) =>
      list(r.content)
        .filter(isObj)
        .slice(0, MAX_COLUMNS)
        .map((c) => cell(c.content, depth + 2)),
    );
  return buildTable(rows, attrsOf(n).widths, attrsOf(n).firstColBold !== false);
}

/** Pasted HTML/Tiptap tables: the first row is the header; colspan is flattened into empty cells, rowspan is ignored. */
function normForeignTable(n: Obj, depth: number): TiptapNode[] {
  const rawRows: Obj[] = [];
  const collect = (content: unknown, d: number) => {
    for (const r of list(content)) {
      if (!isObj(r) || d > MAX_DEPTH) continue;
      if (r.type === "tableRow") rawRows.push(r);
      else collect(r.content, d + 1); // thead / tbody wrappers
    }
  };
  collect(n.content, depth);
  const rows = rawRows.map((r) => {
    const cells: TiptapNode[] = [];
    for (const c of list(r.content)) {
      if (!isObj(c) || cells.length >= MAX_COLUMNS) continue;
      cells.push(cell(c.content, depth + 2));
      const span = Number(attrsOf(c).colspan);
      const extra = Number.isInteger(span) && span > 1 ? Math.min(span, MAX_COLUMNS) - 1 : 0;
      for (let i = 0; i < extra && cells.length < MAX_COLUMNS; i += 1) cells.push(cell([], depth + 2));
    }
    return cells;
  });
  return buildTable(rows, null, true);
}

function normBlock(n: unknown, depth: number): TiptapNode[] {
  if (!isObj(n) || depth > MAX_DEPTH || typeof n.type !== "string") return [];
  const attrs = attrsOf(n);
  switch (n.type) {
    case "paragraph":
      return [paragraph(normInline(n.content, depth + 1))];
    case "heading": {
      const level = Number(attrs.level) === 1 ? 1 : 2;
      const text = plainText(n.content, depth + 1).replace(/\s*[\r\n]+\s*/g, " ");
      const node: TiptapNode = { type: "heading", attrs: { level, number: attrs.number !== false } };
      return [text ? { ...node, content: textNode(text) } : node];
    }
    case "bulletList":
    case "orderedList": {
      const items = listItems(n.content, depth);
      return items.length ? [{ type: n.type, content: items }] : [];
    }
    case "codeBlock":
    case "klCodeLine":
      return codeLines(plainText(n.content, depth + 1)).map((line) =>
        line ? { type: "codeBlock", content: textNode(line) } : { type: "codeBlock" },
      );
    case "klCallout":
      return [
        {
          type: "klCallout",
          attrs: { kind: attrs.kind === "warning" ? "warning" : "note", title: str(attrs.title) },
          content: [paragraph(mergeBlocks(normBlocks(n.content, depth + 1)))],
        },
      ];
    case "klKeyValue": {
      const pairs = list(n.content)
        .filter(isObj)
        .map((p) => {
          const content = normInline(p.content, depth + 2);
          const pair: TiptapNode = {
            type: "klKvPair",
            attrs: { key: str(attrsOf(p).key) ?? "" },
          };
          return content.length ? { ...pair, content } : pair;
        });
      return pairs.length ? [{ type: "klKeyValue", content: pairs }] : [];
    }
    case "klGroupList": {
      const groups = list(n.content)
        .filter(isObj)
        .map((g) => ({
          type: "klGroup",
          attrs: { title: str(attrsOf(g).title) ?? "", meta: str(attrsOf(g).meta) },
          content: list(g.content)
            .filter(isObj)
            .map((item) => ({ type: "klGroupItem", content: normCellContent(item.content, depth + 3) })),
        }))
        .filter((g) => g.content.length > 0);
      return groups.length ? [{ type: "klGroupList", content: groups }] : [];
    }
    case "klTable":
      return normKlTable(n, depth);
    case "table":
      return normForeignTable(n, depth);
    case "pageBreak":
      return [{ type: "pageBreak" }];
    case "klImage":
    case "image": {
      const src = str(attrs.src);
      if (!src || !ATTACHMENT_SRC_RE.test(src)) return [];
      return [
        { type: "klImage", attrs: { src, caption: str(attrs.caption) ?? str(attrs.title), alt: str(attrs.alt) } },
      ];
    }
    case "horizontalRule":
    case "hardBreak":
      return [];
    default: {
      if (INLINE_TYPES.has(n.type)) return [paragraph(normInline([n], depth))];
      const content = list(n.content);
      if (content.some((c) => isObj(c) && typeof c.type === "string" && INLINE_TYPES.has(c.type))) {
        return [paragraph(normInline(content, depth + 1))];
      }
      return normBlocks(content, depth + 1);
    }
  }
}

function normBlocks(content: unknown, depth: number): TiptapNode[] {
  return list(content).flatMap((n) => normBlock(n, depth));
}

/**
 * Make any input fit the Klarity schema: unknown nodes are unwrapped or dropped, unknown
 * marks (colour, font, underline, …) are dropped, one mark per text run, H3+ → H2,
 * code blocks are split per line, pasted tables are flattened. Never throws.
 */
export function normalizeTiptap(doc: unknown): TiptapDoc {
  try {
    const source = isObj(doc) ? (doc.type === "doc" ? doc.content : [doc]) : doc;
    // Each top-level block on its own: one unreadable block does not take the rest with it.
    const content = list(source).flatMap((n) => {
      try {
        return normBlock(n, 0);
      } catch {
        return [];
      }
    });
    return { type: "doc", content: content.length ? content : [{ type: "paragraph" }] };
  } catch {
    return { type: "doc", content: [{ type: "paragraph" }] };
  }
}
